import { useToast } from '@/hooks/useToast'
import { useRouter } from 'next/router'

/* blocks */
import Link from '@/atoms/Link/Link'

/* assets */
import { ReactComponent as ArrowRight } from 'public/assets/icons/chevron-right.svg'
import { ReactComponent as FacebookIcon } from 'public/assets/icons/events/facebook.svg'
import { ReactComponent as TwitterIcon } from 'public/assets/icons/events/twitter.svg'
import { ReactComponent as LinkIcon } from 'public/assets/icons/events/link.svg'

interface Props {
  venueAddress: string
  venueMapLink: string
  venueIframeLink: string
}

function EventVenueDetails({
  venueAddress,
  venueMapLink,
  venueIframeLink
}: Props) {
  const router = useRouter()
  const { toast, showToast } = useToast()

  const links = 'https://www.peppercontent.io' + router.asPath
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(links)
      showToast({
        title: 'Link copied to clipboard',
        type: 'success',
        swipeDirection: 'up'
      })
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <section className="font-heading">
      <div className="container | py-6 md:pt-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-8 | mb-5 md:mb-16">
          <div className="md:col-span-5">
            <h2 className="font-heading text-2xl md:text-3xl text-blue-web-90 font-semibold | mb-2 md:mb-3">
              Venue
            </h2>
            <div className="text-base md:text-2xl text-gray-60 | mb-5 md:mb-10 md:mr-10">
              {venueAddress}
            </div>
            <Link
              href={venueMapLink}
              target="_self"
              className="flex items-center | text-base md:text-xl font-semibold text-blue-web-60 | md:mb-5"
            >
              View location on Google Maps{' '}
              <ArrowRight className="ml-2 | text-blue-web-60" />
            </Link>
          </div>
          <div className="md:col-start-6 md:col-end-13 | md:ml-6">
            <iframe
              title="Venue link iframe"
              src={venueIframeLink}
              width="645"
              height="333"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="block | w-full | aspect-auto | mx-auto rounded-lg"
            ></iframe>
          </div>
        </div>

        <div className="flex justify-start md:justify-end items-center | mb-12 md:mb-0">
          <hr className="w-full text-blue-50 hidden md:block" />
          <span className="font-body text-base md:text-xl font-medium text-gray-60 flex-shrink-0 mx-3">
            Share this event
          </span>
          <div className="flex items-center gap-4 md:gap-5 | text-black">
            <a
              href={`https://www.facebook.com/sharer/sharer.php?quote=${SHARE_TEXT}&u=${URL}`}
              target="_blank"
              rel="noreferrer noopener"
              title="Facebook"
            >
              <FacebookIcon width="25" height="25" />
            </a>
            <a
              href={`https://twitter.com/share?text=${SHARE_TEXT}`}
              target="_blank"
              rel="noreferrer noopener"
              title="Twitter"
            >
              <TwitterIcon width="25" height="25" />
            </a>
            <button onClick={copyLink} className="shareicon">
              <LinkIcon width="25" height="25" />
            </button>
            {toast}
          </div>
        </div>
        <hr className="w-full text-blue-50 block md:hidden" />
      </div>
    </section>
  )
}

const URL = 'https://www.peppercontent.io/events/'
const SHARE_TEXT =
  encodeURI(`Pepper Content is touring the United States with 14+ Roundtables across multiple cities :sparkles: I have requested my invite for an evening full of conversations around Gen AI and Content Marketing, you can do it too by clicking on the link below :point_down:
${URL} @pepper_content`)

export default EventVenueDetails
